import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-dark-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="ZEPHYR AI" className="w-10 h-10 rounded-2xl" />
            <span className="text-xl font-bold">ZEPHYR AI</span>
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-gray-400 mb-12">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="prose prose-invert prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Introduction</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Welcome to ZEPHYR AI ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Information We Collect</h2>
              <h3 className="text-xl font-semibold mb-3 text-white">Personal Information</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Register for an account</li>
                <li>Use the application features</li>
                <li>Contact us for support</li>
                <li>Participate in surveys or promotions</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-white">Automatically Collected Information</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                When you use our application, we may automatically collect certain information, including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Device information (model, operating system, unique device identifiers)</li>
                <li>Usage data (features used, time spent, interactions)</li>
                <li>Location data (with your permission)</li>
                <li>Crash reports and performance data</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">How We Use Your Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use the information we collect or receive:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>To provide and maintain our service</li>
                <li>To improve and personalize your experience</li>
                <li>To process your transactions</li>
                <li>To send you updates and promotional materials (with your consent)</li>
                <li>To monitor and analyze usage patterns</li>
                <li>To detect, prevent, and address technical issues</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Image Processing and AI Features</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                ZEPHYR AI processes your images using advanced artificial intelligence to deliver professional editing results:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Images are processed in real-time through secure cloud services</li>
                <li>Your photos are handled temporarily only during the editing process</li>
                <li>We do not permanently store your images on our servers</li>
                <li>Processed results are delivered directly to your device</li>
                <li>We do not use your photos to train AI models or for any other purpose</li>
                <li>You maintain complete ownership and control of your images</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3 text-white">Your Privacy Matters</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We are committed to protecting your privacy:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Your photos are for your eyes only - we never share them with third parties</li>
                <li>All image transfers use secure, encrypted connections</li>
                <li>Safety filters protect against inappropriate content</li>
                <li>You can delete your edits at any time from within the app</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Data Sharing and Disclosure</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may share your information in the following situations:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li><strong>Service Providers:</strong> We may share data with third-party service providers who perform services on our behalf</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>With Your Consent:</strong> We may disclose your information for any other purpose with your consent</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Security & Protection</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Your security is our top priority. We use industry-leading technology to keep your data safe:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>Bank-level encryption protects all data transfers</li>
                <li>Secure cloud infrastructure ensures reliable service</li>
                <li>Regular security updates keep the app protected</li>
                <li>Your images are processed quickly and never stored permanently</li>
                <li>Secure authentication protects your account</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                We continuously work to maintain the highest security standards. Please keep your account credentials private and secure.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Your Privacy Rights</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>The right to access – You have the right to request copies of your personal data</li>
                <li>The right to rectification – You have the right to request correction of inaccurate data</li>
                <li>The right to erasure – You have the right to request deletion of your data</li>
                <li>The right to restrict processing – You have the right to request limitation of data processing</li>
                <li>The right to data portability – You have the right to request transfer of your data</li>
                <li>The right to object – You have the right to object to our processing of your data</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Children's Privacy</h2>
              <p className="text-gray-300 leading-relaxed">
                Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Third-Party Services</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Our application may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may use third-party services such as:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li><strong>Google AdMob:</strong> For displaying advertisements</li>
                <li><strong>Google Analytics:</strong> For app usage analytics and performance monitoring</li>
                <li><strong>Firebase:</strong> For cloud services, authentication, and real-time features</li>
                <li><strong>Payment Processors:</strong> Google Play Billing and Apple In-App Purchase for subscriptions</li>
                <li><strong>Google AI Services:</strong> For AI-powered image processing features</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Each of these services may collect and process data according to their own privacy policies. We recommend reviewing their privacy policies for more information.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Updates to This Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Contact Us</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <ul className="text-gray-300 space-y-2">
                  <li><strong>Email:</strong> info@zeynsoft.com</li>
                  <li><strong>Help:</strong> help@zeynsoft.com</li>
                  <li><strong>Website:</strong> <a href="https://zephyrai.zeynsoft.com" className="text-primary hover:text-primary/80 transition">https://zephyrai.zeynsoft.com</a></li>
                  <li><strong>Address:</strong> Yenişehir, Mersin, Turkey</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-4 text-white">Consent</h2>
              <p className="text-gray-300 leading-relaxed">
                By using our application, you consent to our Privacy Policy and agree to its terms.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>© {new Date().getFullYear()} ZEPHYR AI by Zeynsoft. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
